import { computeDefaultModeFor } from "@/modes";
import { ModeNames } from "@/modes/mode";
import {
  isElementPhotosGroup,
  TimestampElement,
} from "@/modes/walk-mode/walk-overlay-selectors";
import {
  FeaturesState,
  selectFeaturesSlice,
} from "@/store/features/features-slice";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { selectShouldUseIntensityData } from "@/store/modes/walk-mode-slice";
import { selectProjectFilteringSlice } from "@/store/project-filtering/project-filtering-selectors";
import { ProjectFilteringSlice } from "@/store/project-filtering/project-filtering-slice";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { selectSubscriptionFeaturesSlice } from "@/store/subscription-features/subscription-features-selectors";
import { SubscriptionFeaturesState } from "@/store/subscription-features/subscription-features-slice";
import {
  computeFilterFunctions,
  isIElementFiltered,
} from "@/utils/tree-generation/tree-filters-utils";
import { Option } from "@faro-lotv/flat-ui";
import { IElementsState } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";

/**
 * @returns A memoized selector that computes the mode computed options for the timestamp dropdown elements
 * @param state the state of the app store
 * @param sortedOptions the sorted list of dropdown options to compute the modes for
 */
export const selectModeComputedOptions = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectSubscriptionFeaturesSlice,
      selectFeaturesSlice,
      selectShouldUseIntensityData,
      selectProjectFilteringSlice,
      (state: RootState, sortedOptions: TimestampElement[]) => sortedOptions,
    ],
    computeModeComputedOptions,
  ),
);

function computeModeComputedOptions(
  iElements: IElementsState,
  subscriptions: SubscriptionFeaturesState,
  features: FeaturesState,
  shouldUseIntensityData: boolean,
  filtering: ProjectFilteringSlice,
  sortedOptions: TimestampElement[],
): Array<
  TimestampElement & { mode: ModeNames | undefined } & Pick<
      Option,
      "isFilteredOut"
    >
> {
  const filterFunctions = computeFilterFunctions(filtering);
  // photo group contains IElementImg2d elements of a same day and the time stamp of the first element
  // is used as the time stamp of group. The filtered out status of the first photo element is used as
  // filtered status for the photo group
  return sortedOptions.map((option) => ({
    ...option,
    isFilteredOut:
      filterFunctions.length > 0 &&
      isIElementFiltered(
        isElementPhotosGroup(option) ? option.photos[0] : option,
        iElements.iElements,
        filterFunctions,
      ),
    mode: isElementPhotosGroup(option)
      ? "walk"
      : computeDefaultModeFor(
          iElements,
          subscriptions,
          features,
          option,
          shouldUseIntensityData,
        )?.targetMode,
  }));
}
